import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { VaccineChartResponse } from '@/types/patient-chart-api';

/**
 * Patient Vaccine Chart / Immunization Timeline — Chart > Immunizations >
 * "Vaccine Chart" modal (PROD-827 Slice 2).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:1228-1241`):
 *
 *   GET /api/Patients/GetImmunizationTimelineByPatientId?PatientId={id}
 *       → VaccineChartResponse
 *
 * The response is a different shape from the per-record list at
 * `GetImmunizationByPatientId` — it groups records by vaccine CATEGORY
 * (e.g. Hep B, DTaP, MMR) and ships the expected dose count per
 * category alongside the records that have been administered. The UI
 * renders that as a dose-slot timeline so a clinician can see at a
 * glance which doses are still missing.
 *
 * Lazy-fetched: the timeline endpoint is more expensive than the
 * per-record list, so we only fire it when the modal opens. The
 * `enabled` gate is set at the call site.
 */

export function usePatientVaccineChart(
  patientId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['patient-vaccine-chart', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ PatientId: patientId ?? '' });
      return get<VaccineChartResponse>(
        `/api/Patients/GetImmunizationTimelineByPatientId?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId && enabled,
    staleTime: 60_000,
  });
}
